// src/hooks/usePersistence.js
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase, hasSupabase } from "../lib/supabaseClient";
import { STOCK_UNIVERSE } from "../data/stocks";

const DEFAULT_HOLDINGS = [
  { ...STOCK_UNIVERSE[0], weight: 25 },
  { ...STOCK_UNIVERSE[1], weight: 20 },
  { ...STOCK_UNIVERSE[2], weight: 15 },
];
const DEFAULT_WATCHLIST = ["AAPL","MSFT","GOOGL","AMZN","NVDA","SPY","QQQ","BTC-USD"];

const LS = {
  get: (key, fb) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb; } catch { return fb; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} },
};

async function upsertRow(table, data, userId) {
  const payload = { user_id: userId, ...data, updated_at: new Date().toISOString() };
  const { error } = await supabase
    .from(table)
    .upsert(payload, { onConflict: "user_id" });
  if (error) throw new Error(`UPSERT failed: ${error.message}`);
}

export function usePersistence(supabaseUserId, tier = "free") {
  const USER_ID = supabaseUserId ?? null;
  const [holdings,      setHoldings]      = useState(() => LS.get("holdings",      DEFAULT_HOLDINGS));
  const [riskTolerance, setRiskTolerance] = useState(() => LS.get("riskTolerance", 50));
  const [watchlist,     setWatchlist]     = useState(() => LS.get("mw_watchlist",  DEFAULT_WATCHLIST));
  const [syncStatus,    setSyncStatus]    = useState("idle");
  const [loaded,        setLoaded]        = useState(false);

  const saveTimer  = useRef(null);
  const watchTimer = useRef(null);

  // Cancel pending debounce timers on unmount to prevent cross-test leakage
  useEffect(() => {
    return () => {
      clearTimeout(saveTimer.current);
      clearTimeout(watchTimer.current);
    };
  }, []);

  // ── fetchPortfolio: load holdings + watchlist from Supabase ──
  const fetchPortfolio = useCallback(async () => {
    if (!hasSupabase || !USER_ID) { setLoaded(true); return; }
    try {
      const { data: ptf, error: pe } = await supabase
        .from("portfolios").select("holdings,risk_tolerance")
        .eq("user_id", USER_ID).order("updated_at", { ascending: false }).limit(1); // eslint-disable-line
      if (pe) throw pe;

      if (ptf?.length) {
        const merged = (ptf[0].holdings ?? []).map(h => ({ ...h }));
        setHoldings(merged);
        setRiskTolerance(ptf[0].risk_tolerance ?? 50);
        LS.set("holdings", merged);
        LS.set("riskTolerance", ptf[0].risk_tolerance ?? 50);
      }

      const { data: wl, error: we } = await supabase
        .from("watchlists").select("tickers")
        .eq("user_id", USER_ID).order("updated_at", { ascending: false }).limit(1); // eslint-disable-line
      if (we) throw we;

      if (wl?.length) {
        const tickers = wl[0].tickers ?? DEFAULT_WATCHLIST;
        setWatchlist(tickers);
        LS.set("mw_watchlist", tickers);
      }
    } catch (err) {
      console.warn("[usePersistence] load error:", err.message);
    } finally {
      setLoaded(true);
    }
  }, [USER_ID]); // eslint-disable-line

  // Initial load
  useEffect(() => { fetchPortfolio(); }, [fetchPortfolio]);

  // ── Supabase Realtime: sync holdings when changed from another tab/device ──
  // Deferred until after initial load so WebSocket setup doesn't compete with
  // the first data fetch and slow down the initial render.
  useEffect(() => {
    if (!hasSupabase || !USER_ID || !loaded) return;
    const subscription = supabase
      .channel(`portfolio-changes-${USER_ID}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'portfolios',
        filter: `user_id=eq.${USER_ID}`,
      }, () => {
        fetchPortfolio();
      })
      .subscribe();
    return () => { supabase.removeChannel(subscription); };
  }, [USER_ID, loaded, fetchPortfolio]);

  const savePortfolio = useCallback((newHoldings, newRisk) => {
    LS.set("holdings", newHoldings);
    LS.set("riskTolerance", newRisk);
    if (!hasSupabase || !USER_ID) return;

    clearTimeout(saveTimer.current);
    setSyncStatus("saving");
    saveTimer.current = setTimeout(async () => {
      try {
        const slim = newHoldings.map(h => ({
          ticker: h.ticker, name: h.name, sector: h.sector, weight: h.weight,
          pe: h.pe ?? null, pb: h.pb ?? null, evEbitda: h.evEbitda ?? null,
          beta: h.beta ?? 1, expectedReturn: h.expectedReturn ?? 10,
          volatility: h.volatility ?? 20, dividend: h.dividend ?? 0,
        }));
        await upsertRow("portfolios", { holdings: slim, risk_tolerance: newRisk }, USER_ID);
        setSyncStatus("saved");
        setTimeout(() => setSyncStatus("idle"), 2000);
      } catch (err) {
        console.warn("[usePersistence] save error:", err.message);
        setSyncStatus("error");
        setTimeout(() => setSyncStatus("idle"), 4000);
      }
    }, 800);
  }, []); // eslint-disable-line

  const saveWatchlist = useCallback((tickers) => {
    LS.set("mw_watchlist", tickers);
    if (!hasSupabase || !USER_ID) return;
    clearTimeout(watchTimer.current);
    watchTimer.current = setTimeout(async () => {
      try { await upsertRow("watchlists", { tickers }, USER_ID); }
      catch (err) { console.warn("[usePersistence] watchlist error:", err.message); }
    }, 800);
  }, []);

  const setHoldingsAndSave = useCallback((updater) => {
    setHoldings(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setRiskTolerance(rt => { savePortfolio(next, rt); return rt; });
      return next;
    });
  }, [savePortfolio]);

  const setRiskToleranceAndSave = useCallback((val) => {
    setRiskTolerance(val);
    setHoldings(h => { savePortfolio(h, val); return h; });
  }, [savePortfolio]);

  const setWatchlistAndSave = useCallback((updater) => {
    setWatchlist(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveWatchlist(next);
      return next;
    });
  }, [saveWatchlist]);

  const clearUserData = useCallback(async () => {
    if (!hasSupabase || !USER_ID) return;
    try {
      await supabase.from("portfolios").delete().eq("user_id", USER_ID);
      await supabase.from("watchlists").delete().eq("user_id", USER_ID);
    } catch (err) {
      console.warn("[usePersistence] clearUserData error:", err.message);
    }
  }, []);

  return {
    holdings, riskTolerance, watchlist, syncStatus, loaded,
    setHoldings: setHoldingsAndSave,
    setRiskTolerance: setRiskToleranceAndSave,
    setWatchlist: setWatchlistAndSave,
    userId: USER_ID, hasSupabase, clearUserData,
  };
}
